import {
  type FeatureKey,
  type JourneyType,
  type ProgramType,
  ROLE_KEYS,
  type RoleGrantRequest,
  type RoleKey,
} from '@/types/access-control'

export const PLATFORM_ROLE_KEYS = [
  'platform_admin',
  'coach',
  'coach_manager',
  'account_manager',
  'benefits_triage',
] as const satisfies readonly RoleKey[]

const platformRoles = new Set<RoleKey>(PLATFORM_ROLE_KEYS)
const roleCatalog = new Set<string>(ROLE_KEYS)

export const ROLE_LABELS: Record<RoleKey, string> = {
  platform_admin: 'Platform administrator',
  participant: 'Participant',
  coach: 'Coach',
  coach_manager: 'Coach manager',
  hr_admin: 'HR administrator',
  hr_viewer: 'HR viewer',
  department_representative: 'Department representative',
  account_manager: 'Account manager',
  benefits_triage: 'Benefits triage',
}

export const ROLE_SETUP_NOTES: Partial<Record<RoleKey, string>> = {
  participant:
    'This provisions the Participant role, selected program, and its default cohort together. Available pages are limited by the organization’s enabled features.',
  department_representative:
    'Limited to explicitly assigned offboarding tasks and workstreams; this role does not grant general HR Portal access.',
  hr_viewer:
    'Temporarily has the same full Portal access as HR administrator until the final access split is approved.',
  account_manager: 'Account managers also require assigned client organizations.',
  benefits_triage: 'Benefits triage access is limited to assigned cases and clients.',
}

export interface RolePageAccess {
  site: string
  pages: readonly string[]
  note?: string
}

export const PARTICIPANT_PROGRAM_OPTIONS: readonly {
  value: ProgramType
  label: string
  journeyType: JourneyType
  description: string
  requiresSeveranceRelease: boolean
}[] = [
  {
    value: 'everyone',
    label: 'Embrace Everyone',
    journeyType: 'standard',
    description: 'Core career tools and benefits.',
    requiresSeveranceRelease: false,
  },
  {
    value: 'everyone_severance',
    label: 'Embrace Everyone with Severance',
    journeyType: 'outplacement',
    description: 'Severance election first, then core career tools and benefits.',
    requiresSeveranceRelease: true,
  },
  {
    value: 'everyone_plus',
    label: 'Embrace Everyone+',
    journeyType: 'standard',
    description: 'Core career tools, coaching, business, retirement, and benefits.',
    requiresSeveranceRelease: false,
  },
  {
    value: 'everyone_plus_severance',
    label: 'Embrace Everyone+ with Severance',
    journeyType: 'outplacement',
    description: 'Severance election first, then the full Everyone+ menu.',
    requiresSeveranceRelease: true,
  },
  {
    value: 'internal_mobility',
    label: 'Internal Mobility',
    journeyType: 'internal_mobility',
    description: 'Internal career tools without coaching or benefits.',
    requiresSeveranceRelease: false,
  },
  {
    value: 'voluntary_exit',
    label: 'Voluntary Exit',
    journeyType: 'voluntary_exit',
    description: 'Benefits only.',
    requiresSeveranceRelease: false,
  },
]

const PARTICIPANT_PROGRAM_PAGES: Record<
  ProgramType,
  readonly { label: string; feature: FeatureKey | null }[]
> = {
  everyone: [
    { label: 'Profile', feature: null },
    { label: 'Resume', feature: 'external_jobs' },
    { label: 'Jobs', feature: 'external_jobs' },
    { label: 'Agents', feature: 'external_jobs' },
    { label: 'Skills Map', feature: 'skills_map' },
    { label: 'Benefits', feature: 'benefits' },
  ],
  everyone_severance: [
    { label: 'Profile', feature: null },
    { label: 'Resume', feature: 'external_jobs' },
    { label: 'Jobs', feature: 'external_jobs' },
    { label: 'Agents', feature: 'external_jobs' },
    { label: 'Skills Map', feature: 'skills_map' },
    { label: 'Benefits', feature: 'benefits' },
  ],
  everyone_plus: [
    { label: 'Profile', feature: null },
    { label: 'Resume', feature: 'external_jobs' },
    { label: 'Jobs', feature: 'external_jobs' },
    { label: 'Agents', feature: 'external_jobs' },
    { label: 'Skills Map', feature: 'skills_map' },
    { label: 'Coaching', feature: 'coaching' },
    { label: 'Start a Business', feature: 'coaching' },
    { label: 'Retirement', feature: 'coaching' },
    { label: 'Benefits', feature: 'benefits' },
  ],
  everyone_plus_severance: [
    { label: 'Profile', feature: null },
    { label: 'Resume', feature: 'external_jobs' },
    { label: 'Jobs', feature: 'external_jobs' },
    { label: 'Agents', feature: 'external_jobs' },
    { label: 'Skills Map', feature: 'skills_map' },
    { label: 'Coaching', feature: 'coaching' },
    { label: 'Start a Business', feature: 'coaching' },
    { label: 'Retirement', feature: 'coaching' },
    { label: 'Benefits', feature: 'benefits' },
  ],
  internal_mobility: [
    { label: 'Profile', feature: null },
    { label: 'Resume', feature: 'internal_jobs' },
    { label: 'Jobs (internal only)', feature: 'internal_jobs' },
    { label: 'Agents', feature: 'external_jobs' },
    { label: 'Skills Map', feature: 'skills_map' },
  ],
  voluntary_exit: [{ label: 'Benefits', feature: 'benefits' }],
}

export function getParticipantProgramOption(program: ProgramType) {
  return PARTICIPANT_PROGRAM_OPTIONS.find((option) => option.value === program)
}

export function getParticipantProgramPageAccess(
  program: ProgramType,
  organizationFeatures: Record<string, boolean>,
): readonly RolePageAccess[] {
  const option = getParticipantProgramOption(program)
  const pages = PARTICIPANT_PROGRAM_PAGES[program]
    .filter((page) => page.feature === null || organizationFeatures[page.feature] === true)
    .map((page) => page.label)
  return [
    {
      site: 'Embrace',
      pages,
      note: [
        option?.description,
        option?.requiresSeveranceRelease
          ? 'Benefits election is the only accessible participant journey until release.'
          : undefined,
        'This preview applies the organization’s current entitlements; frontend release flags can further limit optional pages.',
      ]
        .filter(Boolean)
        .join(' '),
    },
  ]
}

const FULL_PORTAL_PAGE_ACCESS: readonly RolePageAccess[] = [
  {
    site: 'Portal',
    pages: [
      'Dashboard',
      'People',
      'Import & history',
      'Unmatched rows',
      'Stakeholders',
      'Internal jobs',
      'Severance elections',
      'Import terms',
      'Cost & savings',
      'Exit guide',
      'HR system',
    ],
    note: 'Optional pages still depend on organization entitlements and build configuration.',
  },
]

export const ROLE_PAGE_ACCESS: Record<RoleKey, readonly RolePageAccess[]> = {
  platform_admin: [
    {
      site: 'Console',
      pages: [
        'Dashboard',
        'Users & access',
        'Coach review',
        'Job corpus',
        'Crisis alerts',
        'Organizations',
        'Import',
        'Analytics',
        'Content libraries',
        'Dead letter queues',
        'Observability',
      ],
    },
    { site: 'Coach', pages: ['Overview', 'Availability', 'Messages', 'Profile'] },
    {
      site: 'Coach Manager',
      pages: [
        'Operations',
        'Matching queue',
        'Candidates',
        'Coach bench',
        'Coach review',
        'Reviews',
      ],
    },
  ],
  participant: [
    {
      site: 'Embrace',
      pages: ['Profile', 'Resume', 'Opportunities', 'Skills Map', 'Coaching', 'Benefits', 'Learn'],
      note: 'The active program, cohort, entitlements, and workflow decide which of these pages are available.',
    },
  ],
  coach: [{ site: 'Coach', pages: ['Overview', 'Availability', 'Messages', 'Profile'] }],
  coach_manager: [
    { site: 'Coach', pages: ['Overview', 'Availability', 'Messages', 'Profile'] },
    {
      site: 'Coach Manager',
      pages: [
        'Operations',
        'Matching queue',
        'Candidates',
        'Coach bench',
        'Coach review',
        'Reviews',
      ],
    },
  ],
  hr_admin: FULL_PORTAL_PAGE_ACCESS,
  hr_viewer: FULL_PORTAL_PAGE_ACCESS,
  department_representative: [
    {
      site: 'Offboarding tasks',
      pages: ['Assigned workstreams'],
      note: 'A dedicated stakeholder workspace is not available in the frontend yet.',
    },
  ],
  account_manager: [
    {
      site: 'Console',
      pages: [],
      note: 'The assigned-client account management workspace is not available in the frontend yet.',
    },
  ],
  benefits_triage: [
    {
      site: 'Console',
      pages: [],
      note: 'The assigned-case benefits triage workspace is not available in the frontend yet.',
    },
  ],
}

export function isPlatformRole(role: RoleKey): boolean {
  return platformRoles.has(role)
}

export type GrantRequestResult =
  | { ok: true; request: RoleGrantRequest }
  | { ok: false; error: string }

export function buildRoleGrantRequest(input: {
  role: string
  organizationId: number | null
  expiresAt: string
  reason: string
  now?: Date
}): GrantRequestResult {
  if (!roleCatalog.has(input.role)) return { ok: false, error: 'Select a supported role.' }
  const role = input.role as RoleKey
  const platform = isPlatformRole(role)
  if (!platform && (!Number.isInteger(input.organizationId) || (input.organizationId ?? 0) < 1)) {
    return { ok: false, error: 'Select an organization for this role.' }
  }
  if (platform && input.organizationId !== null) {
    return { ok: false, error: 'Platform roles cannot be limited to an organization.' }
  }
  const reason = input.reason.trim()
  if (reason.length < 3) return { ok: false, error: 'Enter a reason of at least 3 characters.' }
  if (reason.length > 2000) return { ok: false, error: 'Reason must be 2,000 characters or less.' }

  let expiresAt: string | null = null
  if (input.expiresAt) {
    const parsed = new Date(input.expiresAt)
    if (Number.isNaN(parsed.getTime())) return { ok: false, error: 'Enter a valid expiry date.' }
    if (parsed.getTime() <= (input.now ?? new Date()).getTime()) {
      return { ok: false, error: 'Expiry must be in the future.' }
    }
    expiresAt = parsed.toISOString()
  }

  return {
    ok: true,
    request: {
      role,
      organization_id: platform ? null : input.organizationId,
      expires_at: expiresAt,
      reason,
    },
  }
}

export function validateRevokeReason(reason: string): string | null {
  const trimmed = reason.trim()
  if (trimmed.length < 3) return 'Enter a reason of at least 3 characters.'
  if (trimmed.length > 2000) return 'Reason must be 2,000 characters or less.'
  return null
}
